import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { logMerchantTypeChange } from '@/utils/activityLogger';

export type MerchantType = 'direct' | 'reseller';

interface MerchantTypeSelectorProps {
  organisationId: string;
  organisationName: string;
  currentType: MerchantType;
  onTypeChange?: (newType: MerchantType) => void;
  displayOnly?: boolean;
}

export const getMerchantTypeLabel = (type: MerchantType): string => {
  return type === 'direct' ? 'Direct' : 'Reseller';
};

export const getMerchantTypeBadgeVariant = (type: MerchantType): 'default' | 'secondary' => {
  return type === 'direct' ? 'default' : 'secondary';
};

export const MerchantTypeBadge: React.FC<{ type: MerchantType }> = ({ type }) => {
  return (
    <Badge 
      variant={getMerchantTypeBadgeVariant(type)}
      className={type === 'direct' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' : 'bg-purple-100 text-purple-800 hover:bg-purple-100'}
    >
      {getMerchantTypeLabel(type)}
    </Badge>
  );
};

const MerchantTypeSelector: React.FC<MerchantTypeSelectorProps> = ({
  organisationId,
  organisationName,
  currentType,
  onTypeChange,
  displayOnly = false,
}) => {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedType, setSelectedType] = useState<MerchantType>(currentType);

  const handleTypeChange = async (newType: MerchantType) => {
    if (newType === selectedType) return;

    setIsUpdating(true);
    const oldType = selectedType;

    try {
      // Get current user for audit log
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Update the organisation's merchant type
      const { error: updateError } = await supabase
        .from('organisations')
        .update({ merchant_type: newType })
        .eq('id', organisationId);

      if (updateError) {
        throw updateError;
      }

      // Log the change in user activity logs
      await logMerchantTypeChange(
        user.email || 'unknown',
        organisationId,
        organisationName,
        oldType,
        newType
      );

      setSelectedType(newType);
      onTypeChange?.(newType);

      toast({
        title: 'Merchant Type Updated',
        description: `Changed from ${getMerchantTypeLabel(oldType)} to ${getMerchantTypeLabel(newType)}`,
      });
    } catch (error) {
      console.error('Error updating merchant type:', error);
      toast({
        title: 'Error',
        description: 'Failed to update merchant type',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (displayOnly) {
    return <MerchantTypeBadge type={selectedType} />;
  }

  return (
    <div className="flex items-center gap-2">
      <Select
        value={selectedType}
        onValueChange={(value) => handleTypeChange(value as MerchantType)}
        disabled={isUpdating}
      >
        <SelectTrigger 
          className="w-[120px] h-8" 
          data-testid="merchant-type-selector"
          onClick={(e) => e.stopPropagation()}
        >
          {isUpdating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <SelectValue />
          )}
        </SelectTrigger>
        <SelectContent onClick={(e) => e.stopPropagation()}>
          <SelectItem value="direct" data-testid="merchant-type-direct">Direct</SelectItem>
          <SelectItem value="reseller" data-testid="merchant-type-reseller">Reseller</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

export default MerchantTypeSelector;
